import { IWasmCustomData, TWasmId } from "~/interfaces";
import { getTextureName } from "./utils";
import Wasm from "./wasm";
import { TWasmTextureNames, WASM_ALL_TEXTURE_NAMES } from "./wasm-textures-data";
import { getPathCaption, getReview, getRoutePointCircle, getText } from "./wasm-textures-utils";
import { WASM_TEXTURES } from "./const";
import { TUrlProviderNamesVariants } from "./interfaces";

// @ts-ignore
export const MAIN_TEXTURES: {[key in TWasmTextureNames] : string} = {};
/* eslint-disable-next-line */
const AUTO_TEXTURES: Array<TWasmTextureNames> = ["pano", "measure-start-active", "measure-start-deactive", "measure-points-active", "measure-points-deactive", "height-profile", "always-on-map", "little-active", "little-deactive",
/* eslint-disable-next-line */
	"universal-active", "universal-deactive", "position-share-image-arrow"];
const DEF_CIRCLE_WIDTH_PX = 512;
const CIRCLE_COLORS = {
	geoLocation: "rgba(0, 168, 255, 0.2)",
	positionShare: "rgba(111, 143, 247, 0.3)",
};
const STAR_END_OPACITY = 0.7;

for (const wasmTextureName of WASM_ALL_TEXTURE_NAMES) {
	MAIN_TEXTURES[wasmTextureName] = getTextureName(wasmTextureName);
}

export type TCustomTexturesNames = "geo-location" | "user-marks-active" | "user-marks-deactive" | "user-marks-opacity-active" | "small-active" | "small-deactive" | "route-start-active" | "route-start-deactive" | "route-end-active" | "route-end-deactive" | "route-start-opacity-active"
	| "route-end-opacity-active" | "search-badge-text-active" | "search-badge-text-deactive" | "photo" | "webcam" | "position-share-device-active" | "position-share-device-deactive" | "position-share-image-active" | "position-share-image-deactive"
	| "position-share-image-gray" | "path-caption" | "route-point-circle";

export const CUSTOM_TEXTURES: {[key in TCustomTexturesNames]: string} = {
	"geo-location": getTextureName(`geo-location`, true),
	"user-marks-active": getTextureName(`user-marks-active`, true),
	"user-marks-deactive": getTextureName(`user-marks-deactive`, true),
	"user-marks-opacity-active": getTextureName(`user-marks-opacity-active`, true),
	"small-active": getTextureName(`small-active`, true),
	"small-deactive": getTextureName(`small-deactive`, true),
	"route-start-active": getTextureName(`route-start-active`, true),
	"route-start-deactive": getTextureName(`route-start-deactive`, true),
	"route-end-active": getTextureName(`route-end-active`, true),
	"route-end-deactive": getTextureName(`route-end-deactive`, true),
	"route-start-opacity-active": getTextureName(`route-start-opacity-active`, true),
	"route-end-opacity-active": getTextureName(`route-end-opacity-active`, true),
	"search-badge-text-active": getTextureName(`search-badge-text-active`, true),
	"search-badge-text-deactive": getTextureName(`search-badge-text-deactive`, true),
	"photo": getTextureName(`photo`, true),
	"webcam": getTextureName(`webcam`, true),
	"position-share-device-active": getTextureName(`position-share-device-active`, true),
	"position-share-device-deactive": getTextureName(`position-share-device-deactive`, true),
	"position-share-image-active": getTextureName(`position-share-image`, true),
	"position-share-image-deactive": getTextureName(`position-share-deactive`, true),
	"position-share-image-gray": getTextureName(`position-share-gray`, true),
	"path-caption": getTextureName(`path-caption`, true),
	"route-point-circle": getTextureName(`route-point-circle`, true),
};

// 0 se renderuje prvni, pak 1, pak 2...
export const DROP_HIGH_PRIORITY_LAYER = 0;

function getUrlProvider(name: TUrlProviderNamesVariants) {
	return getTextureName(name, true);
}

function autoTextures(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module) {
	for (const textureName of AUTO_TEXTURES) {
		const textureData = wasm.textures.getWasmTexture(textureName);
		const wasmTextureName = MAIN_TEXTURES[textureName];

		mapContent.registerPointElementVisual(wasm.getTextureData(wasmTextureName).id, [
			new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, textureData.width, textureData.height, textureData.anchorX, textureData.anchorY, wasmTextureName),
		]);
	}
}

function geoLocation(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module) {
	const geoLocationData = wasm.textures.getWasmTexture("geo-location");

	mapContent.registerPointElementVisual(wasm.getTextureData(CUSTOM_TEXTURES["geo-location"]).id, [
		(elementId: TWasmId, userData: IWasmCustomData) => {
			if (userData?.radius) {
				const radius = userData.radius;
				const width = userData.radius * 2;
				const url = new URL(getUrlProvider("circle-radius"));

				url.searchParams.append("color", CIRCLE_COLORS.geoLocation);
				url.searchParams.append("width", DEF_CIRCLE_WIDTH_PX.toString());

				return new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Map, width, width, radius, radius, url.toString());
			}

			return null;
		},
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, geoLocationData.width, geoLocationData.height, geoLocationData.anchorX, geoLocationData.anchorY, MAIN_TEXTURES["geo-location"]),
	]);
}

function big(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, textureName: TWasmTextureNames) {
	const texture = MAIN_TEXTURES[textureName];
	const bigActiveData = wasm.textures.getWasmTexture(textureName);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, bigActiveData.width, bigActiveData.height, bigActiveData.anchorX, bigActiveData.anchorY, texture),
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, 30, 30, 15, 57, (elementId, userData) => {
			if (userData) {
				return userData.url;
			}

			return "";
		}),
	]);
}

interface IUserMarksConfig {
	active: boolean;
	opacity: number;
}

function userMarks(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, configArg?: Partial<IUserMarksConfig>) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const config: IUserMarksConfig = {
		active: true,
		opacity: 1,
		...configArg,
	};
	const textureName: TWasmTextureNames = config.active ? "small-active" : "small-deactive";
	const smallData = wasm.textures.getWasmTexture(textureName);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, smallData.width, smallData.height, smallData.anchorX, smallData.anchorY, (elementId, userData) => {
			if (userData) {
				const url = new URL(getUrlProvider("user-marks"));

				url.searchParams.append("active", config.active ? "1" : "0");
				url.searchParams.append("opacity", config.opacity.toString());
				url.searchParams.append("order", userData.order.toString());

				return url.toString();
			}

			return "";
		}),
	]);
}

function small(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, isActive: boolean) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const textureName: TWasmTextureNames = isActive ? "small-active" : "small-deactive";
	const smallActiveData = wasm.textures.getWasmTexture(textureName);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, smallActiveData.width, smallActiveData.height, smallActiveData.anchorX, smallActiveData.anchorY, MAIN_TEXTURES[textureName]),
		(elementId: TWasmId, userData: IWasmCustomData) => {
			if (userData?.url || userData?.textureName) {
				const parts: Array<Module.PointElementPart> = [];
				const size = userData.url
					? 20
					: 24;
				const offset = userData.url
					? 0
					: 2;
				let imgTextureName = "";

				if (userData.textureName) {
					const url = new URL(getTextureName(userData.textureName));

					imgTextureName = url.toString();
				} else {
					imgTextureName = userData.url;
				}

				parts.push(new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, size, size, 10 + offset, 38 + offset, imgTextureName));

				return parts;
			}

			return null;
		},
	]);
}

interface IRouteConfig {
	active: boolean;
	start: boolean;
	opacity: number;
}

function route(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, configArg?: Partial<IRouteConfig>) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const config: IRouteConfig = {
		active: true,
		start: true,
		opacity: 1,
		...configArg,
	};
	const bigActiveData = wasm.textures.getWasmTexture(config.active ? "big-active" : "big-deactive");
	const url = new URL(getUrlProvider("route"));

	url.searchParams.append("active", config.active ? "1" : "0");
	url.searchParams.append("start", config.start ? "1" : "0");
	url.searchParams.append("opacity", config.opacity.toString());

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, bigActiveData.width, bigActiveData.height, bigActiveData.anchorX, bigActiveData.anchorY, url.toString()),
	]);
}

function searchBadgeText(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: string, textureName: TWasmTextureNames) {
	const smallActiveData = wasm.textures.getWasmTexture(textureName);
	const groupDrop = {
		name: "drop",
		value: 0,
	};
	const groupTexts = {
		name: "texts",
		value: 1,
	};

	mapContent.registerPointElementVisual(wasm.getTextureData(bindTexture).id, [
		new module.CollLogicGroup(groupDrop.name, groupDrop.value, module.CollGroupMode.CollisionSrcOnly),
		new module.CollLogicGroup(groupTexts.name, groupTexts.value, module.CollGroupMode.SinglePartVisible),
	], [
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, smallActiveData.width, smallActiveData.height, smallActiveData.anchorX, smallActiveData.anchorY, MAIN_TEXTURES[textureName], groupDrop.name, 0),
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, 20, 20, 10, 38, (elementId, userData) => {
			if (userData) {
				return userData.url;
			}

			return "";
		}, groupDrop.name, 1),
		(elementId: TWasmId, userData: IWasmCustomData) => {
			if (userData?.search) {
				// musime dat do pole, pak vratit, jinak to ve wasm nefakci
				const parts: Array<Module.PointElementPart> = [];
				const ratio = WASM_TEXTURES.ratio;
				const reviewTexture = getReview(userData.search.reviewValue || 5, userData.search.reviewActive);
				const reviewWidth = reviewTexture.canvas.width / ratio;
				const reviewHeight = reviewTexture.canvas.height / ratio;

				if (userData.search.reviewValue) {
					const reviewUrl = new URL(getUrlProvider("search-caption-review"));

					reviewUrl.searchParams.append("value", userData.search.reviewValue.toString());
					reviewUrl.searchParams.append("active", userData.search.reviewActive ? "1" : "0");

					parts.push(new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, reviewWidth, reviewHeight, -reviewWidth / 4, smallActiveData.height + (reviewHeight / 2), reviewUrl.toString()));
				}

				if (userData.caption) {
					const textTexture = getText(userData.caption);
					const width = textTexture.canvas.width / ratio;
					const height = textTexture.canvas.height / ratio;
					const url = new URL(getUrlProvider("search-caption-text"));
					const leftRightPadding = 2;

					url.searchParams.append("text", userData.caption);

					// text on right side of drop
					parts.push(new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, width, height, -smallActiveData.anchorX - leftRightPadding, (smallActiveData.height / 2) + (height / 2), url.toString(), groupTexts.name, 0));
					// the same text on top of arrow,
					parts.push(new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, width, height, (width / 2) - (smallActiveData.anchorX / 2), smallActiveData.height + (height / 2) + reviewHeight, url.toString(), groupTexts.name, 1));
					// the same text on bottom arrow,
					parts.push(new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, width, height, (width / 2) - (smallActiveData.anchorX / 2), 0, url.toString(), groupTexts.name, 2));
					// the same text on left arrow,
					parts.push(new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, width, height, smallActiveData.anchorX + width + leftRightPadding, (smallActiveData.height / 2) + (height / 2), url.toString(), groupTexts.name, 3));
				}

				if (parts.length === 0) {
					return null;
				}

				return parts;
			}

			return null;
		},
	]);
}

function photo(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const photoData = wasm.textures.getWasmTexture("photo");

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, photoData.width, photoData.height, photoData.anchorX, photoData.anchorY, MAIN_TEXTURES.photo),
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, 25, 25, 13, 13, (elementId, userData) => {
			if (userData) {
				return userData.url;
			}

			return "";
		}),
	]);
}

function webcam(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const webcamData = wasm.textures.getWasmTexture("webcam");

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, webcamData.width, webcamData.height, webcamData.anchorX, webcamData.anchorY, MAIN_TEXTURES.webcam),
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, 56, 41, 29, 21, (elementId, userData) => {
			if (userData) {
				return userData.url;
			}

			return "";
		}),
	]);
}

function positionShareDevice(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, isActive: boolean) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const positionTexture = wasm.textures.getWasmTexture(isActive ? "position-share-device-active" : "position-share-device-deactive");

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		(elementId: TWasmId, userData: IWasmCustomData) => {
			if (userData?.radius) {
				const radius = userData.radius;
				const width = userData.radius * 2;
				const url = new URL(getUrlProvider("circle-radius"));

				url.searchParams.append("color", CIRCLE_COLORS.positionShare);
				url.searchParams.append("width", DEF_CIRCLE_WIDTH_PX.toString());

				return new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Map, width, width, radius, radius, url.toString());
			}

			return null;
		},
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, positionTexture.width, positionTexture.height, positionTexture.anchorX, positionTexture.anchorY, (elementId, userData) => {
			if (userData?.order) {
				const url = new URL(getUrlProvider("position-share-device"));

				url.searchParams.append("order", userData.order.toString());
				url.searchParams.append("active", isActive ? "1" : "0");

				return url.toString();
			}

			return "";
		}),
	]);
}

type TPositionShareImageVariant = "active" | "deactive" | "gray";

function positionShareImage(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames, variant: TPositionShareImageVariant) {
	const texture = CUSTOM_TEXTURES[bindTexture];
	const textureName: TWasmTextureNames = variant === "active"
		? "position-share-image-active"
		: "position-share-image-deactive";
	const positionTexture = wasm.textures.getWasmTexture(textureName);

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		(elementId: TWasmId, userData: IWasmCustomData) => {
			if (userData?.radius) {
				const radius = userData.radius;
				const width = userData.radius * 2;
				const url = new URL(getUrlProvider("circle-radius"));

				url.searchParams.append("color", CIRCLE_COLORS.positionShare);
				url.searchParams.append("width", DEF_CIRCLE_WIDTH_PX.toString());

				return new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Map, width, width, radius, radius, url.toString());
			}

			return null;
		},
		new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, positionTexture.width, positionTexture.height, positionTexture.anchorX, positionTexture.anchorY, (elementId, userData) => {
			if (userData?.url) {
				const url = new URL(getUrlProvider("position-share-image"));

				url.searchParams.append("img", userData.url);
				url.searchParams.append("active", variant === "active" ? "1" : "0");
				url.searchParams.append("gray", variant === "gray" ? "1" : "0");

				return url.toString();
			}

			return "";
		}),
	]);
}

function pathCaption(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		(elementId, userData) => {
			if (userData?.caption) {
				const captionTexture = getPathCaption(userData.caption);
				const parts: Array<Module.PointElementPart> = [];
				const ratio = WASM_TEXTURES.ratio;
				const width = captionTexture.canvas.width / ratio;
				const height = captionTexture.canvas.height / ratio;
				const url = new URL(getUrlProvider("path-caption"));

				url.searchParams.append("text", userData.caption);
				parts.push(new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, width, height, width / 2, height / 2, url.toString()));

				return parts;
			}

			return null;
		},
	]);
}

function routePointCircle(wasm: Wasm, mapContent: Module.ContentService, module: typeof Module, bindTexture: TCustomTexturesNames) {
	const texture = CUSTOM_TEXTURES[bindTexture];

	mapContent.registerPointElementVisual(wasm.getTextureData(texture).id, [
		(elementId, userData) => {
			if (userData?.routePoint) {
				const captionTexture = getRoutePointCircle(userData.routePoint.radius, userData.routePoint.color);
				const parts: Array<Module.PointElementPart> = [];
				const ratio = WASM_TEXTURES.ratio;
				const width = captionTexture.canvas.width / ratio;
				const height = captionTexture.canvas.height / ratio;
				const url = new URL(getUrlProvider("route-point-circle"));

				url.searchParams.append("radius", userData.routePoint.radius.toString());
				url.searchParams.append("color", userData.routePoint.color);
				parts.push(new module.PointElementPart(DROP_HIGH_PRIORITY_LAYER, module.SizeMode.Pixel, width, height, width / 2, height / 2, url.toString()));

				return parts;
			}

			return null;
		},
	]);
}

export function registerTextures(wasm: Wasm) {
	const mapContent = wasm.mapContent;
	const module = wasm.module;

	// vycet auto ikon
	autoTextures(wasm, mapContent, module);
	// geo-location
	geoLocation(wasm, mapContent, module);
	// big-active/deactive
	big(wasm, mapContent, module, "big-active");
	big(wasm, mapContent, module, "big-deactive");
	// user-marks-active/deactive
	userMarks(wasm, mapContent, module, "user-marks-active");
	userMarks(wasm, mapContent, module, "user-marks-deactive", { active: false });
	userMarks(wasm, mapContent, module, "user-marks-opacity-active", { opacity: STAR_END_OPACITY });
	// small-active/deactive
	small(wasm, mapContent, module, "small-active", true);
	small(wasm, mapContent, module, "small-deactive", false);
	// route-start-active/deactive
	route(wasm, mapContent, module, "route-start-active");
	route(wasm, mapContent, module, "route-start-deactive", { active: false });
	// route-end-active/deactive
	route(wasm, mapContent, module, "route-end-active", { start: false });
	route(wasm, mapContent, module, "route-end-deactive", { active: false, start: false });
	// route-start-opacity-active/deactive
	route(wasm, mapContent, module, "route-start-opacity-active", { opacity: STAR_END_OPACITY });
	route(wasm, mapContent, module, "route-end-opacity-active", { start: false, opacity: STAR_END_OPACITY });
	// search-badge-text-active/deactive
	searchBadgeText(wasm, mapContent, module, CUSTOM_TEXTURES["search-badge-text-active"], "small-active");
	searchBadgeText(wasm, mapContent, module, CUSTOM_TEXTURES["search-badge-text-deactive"], "small-deactive");
	// photo
	photo(wasm, mapContent, module, "photo");
	// webcam
	webcam(wasm, mapContent, module, "webcam");
	// position-share-device-active/deactive
	positionShareDevice(wasm, mapContent, module, "position-share-device-active", true);
	positionShareDevice(wasm, mapContent, module, "position-share-device-deactive", false);
	// position-share-image-active|deactive|gray
	positionShareImage(wasm, mapContent, module, "position-share-image-active", "active");
	positionShareImage(wasm, mapContent, module, "position-share-image-deactive", "deactive");
	positionShareImage(wasm, mapContent, module, "position-share-image-gray", "gray");
	// path-caption
	pathCaption(wasm, mapContent, module, "path-caption");
	// route-point-circle
	routePointCircle(wasm, mapContent, module, "route-point-circle");
}
